/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @angular-eslint/prefer-on-push-component-change-detection */
/* eslint-disable @angular-eslint/prefer-signals */

import { Component, Input, type OnDestroy, type OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, Subject } from 'rxjs';
import { debounceTime, finalize, map, takeUntil } from 'rxjs/operators';
import { v4 } from 'uuid';

import { AllowedDomain } from '@entities/platform-entities/allowed-domain/allowed-domain.entity';
import { AVON_BRAZIL_INTEGRATION_CLASS } from '@entities/platform-entities/integration/avon-brazil-integration-class.const';
import type { CsvTerrificField } from '@entities/platform-entities/integration/csv-terrific-field.type';
import type { CsvVariantGroupingStrategy } from '@entities/platform-entities/integration/csv-variant-grouping-strategy.type';
import { GENERIC_CSV_INTEGRATION_CLASS } from '@entities/platform-entities/integration/generic-csv-integration-class.const';
import { Integration } from '@entities/platform-entities/integration/integration.entity';
import { NATURA_BRAZIL_INTEGRATION_CLASS } from '@entities/platform-entities/integration/natura-brazil-integration-class.const';
import { SFCC_INTEGRATION_CLASS } from '@entities/platform-entities/integration/sfcc-integration-class.const';
import { TIENDANUBE_INTEGRATION_CLASS } from '@entities/platform-entities/integration/tiendanube-integration-class.const';
import { URBANICA_INTEGRATION_CLASS } from '@entities/platform-entities/integration/urbanica-integration-class.const';
import { BroadcastResolution } from '@entities/platform-entities/store/enums/broadcast-resolution.enum';
import { StoreCategory } from '@entities/platform-entities/store/enums/store-category.enum';
import { CustomTheme } from '@entities/platform-entities/store/interfaces/custom-theme.interface';
import { LEAD_IMAGE_POSITION } from '@entities/platform-entities/store/lead-image-position.const';
import { Store } from '@entities/platform-entities/store/store.entity';

import { CsvFeedPreviewService } from '../../services/csv-feed-preview.service';
import { StoreCatalogUploadService } from '../../services/store-catalog-upload.service';
import { StoresService } from '../../services/stores.service';
import { TiendanubeAppService } from '../../services/tiendanube-app.service';
import { UsersService } from '../../services/users.service';

import { applyCsvFeedPresets } from '../../helpers/csv-helpers/apply-csv-feed-presets';
import { CSV_OPTIONAL_TERRIFIC_FIELDS } from '../../helpers/csv-helpers/csv-optional-terrific-fields';
import { CSV_REQUIRED_TERRIFIC_FIELDS } from '../../helpers/csv-helpers/csv-required-terrific-fields';
import { CSV_TERRIFIC_FIELD_LABELS } from '../../helpers/csv-helpers/csv-terrific-field-labels';
import { CSV_VARIANT_GROUPING_OPTIONS } from '../../helpers/csv-helpers/csv-variant-grouping-options';
import { validateCsvIntegrationConfig } from '../../helpers/csv-helpers/validate-csv-config';
import TwoRaffleHelpers from '../../helpers/helpers';
import { applySfccIntegrationDefaults } from '../../helpers/sfcc-helpers/apply-sfcc-integration-defaults';
import { isEnteringSfccIntegration } from '../../helpers/sfcc-helpers/is-entering-sfcc-integration';
import { validateSfccIntegrationConfig } from '../../helpers/sfcc-helpers/validate-sfcc-integration-config';
import { validateCronScheduledSync } from '../../helpers/validate-cron-scheduled-sync';
import { validateCronScheduledSyncTimezone } from '../../helpers/validate-cron-scheduled-sync-timezone';
import { validateIpAllowlist } from '../../helpers/validate-ip-allowlist';

import { CronExpressionEditModalComponent } from '../cron-expression-edit-modal/cron-expression-edit-modal.component';
import type { CronExpressionEditResult } from '../cron-expression-edit-modal/cron-expression-edit-result.interface';

import { isShopifyIntegrationClass } from '../../../../../shared/db-models/integration';
import { type DbCurrencyModel, PaymentSuppliers } from '../../../../../shared/db-models/payments';
import { StoreType, VoteResultsMode } from '../../../../../shared/db-models/store';
import { sanitizeUrl } from '../../../../../shared/lib/storeHelpers';
import { sanitizeStoreInternalURL } from '../../../../../shared/utilities/url-helpers';

const DEFAULT_SESSION_START_TIME_FRAME_HOURS = 24;
const DEFAULT_BROADCAST_RESOLUTION = BroadcastResolution.Res720p30fps;

const DefaultLazyEmbed = {
  desktop: 0.5,
  enabled: false,
  mobile: 1.5,
} as const;

const VISIBLE_PAYMENT_SUPPLIERS: PaymentSuppliers[] = [
  PaymentSuppliers.None,
  PaymentSuppliers.SDKWithRedirect,
  PaymentSuppliers.ShopifyPermalinks,
];

type AllowedDomainBaseline = {
  addressPattern: string;
  isEnabled: boolean;
  updatedAt: AllowedDomain['updatedAt'];
  updatedBy: string;
};

@Component({
  selector: 'app-edit-store-modal',
  standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
  styleUrl: './edit-store-modal.component.scss',
  templateUrl: './edit-store-modal.component.html',
})
export class EditStoreModalComponent implements OnInit, OnDestroy {
  isLoading = true;
  isSubmitting: boolean;
  destroyed$ = new Subject<void>();

  public PaymentSuppliers = PaymentSuppliers;
  public StoreCategory = StoreCategory;
  public storeCategoryOptions = Object.values(StoreCategory);
  public storeTypesOptions = Object.values(StoreType);
  public storesIdsThatSupportColorPalate: string[] = [];
  public hasShopifyAppInstalled = false;
  public showMoreSettings = false;
  public showDevFeatureFlags = false;
  public readonly genericCsvIntegrationClass = GENERIC_CSV_INTEGRATION_CLASS;
  public readonly sfccIntegrationClass = SFCC_INTEGRATION_CLASS;
  public readonly csvRequiredFields = CSV_REQUIRED_TERRIFIC_FIELDS;
  public readonly csvOptionalFields = CSV_OPTIONAL_TERRIFIC_FIELDS;
  public readonly csvFieldLabels = CSV_TERRIFIC_FIELD_LABELS;
  public readonly csvVariantGroupingOptions = CSV_VARIANT_GROUPING_OPTIONS;
  public showCsvOptionalFields = false;
  public csvFeedHeaders: string[] = [];
  public csvFeedPreviewStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  public csvFeedPreviewError = '';
  public csvFeedSampleRowCount = 0;
  public readonly csvTabDelimiter = '\t';
  public isCsvCatalogUploading = false;
  public csvCatalogUploadProgress = 0;

  private devClickCount = 0;
  private readonly csvFeedPreviewRequest$ = new Subject<string>();
  private csvFeedPreviewRequestId = 0;
  private devClickTimer: ReturnType<typeof setTimeout> | null = null;

  onFeatureFlagsLegendClick(): void {
    this.devClickCount++;
    if (this.devClickCount === 1) {
      this.devClickTimer = setTimeout(() => {
        this.devClickCount = 0;
      }, 500);
    }
    if (this.devClickCount >= 3) {
      if (this.devClickTimer) {
        clearTimeout(this.devClickTimer);
        this.devClickTimer = null;
      }
      this.showDevFeatureFlags = !this.showDevFeatureFlags;
      this.devClickCount = 0;
    }
  }
  public productIds = '';
  public paymentSuppliers: { name: string; value: number }[];
  protected readonly leadImagePositions = Array.from(
    { length: LEAD_IMAGE_POSITION.max - LEAD_IMAGE_POSITION.min + 1 },
    (_, index) => index + LEAD_IMAGE_POSITION.min
  );
  private hadInitialPaymentSupplier = false;
  private hadTiendaNubeApplicationAccess = false;
  private allowedDomainBaselines = new Map<string, AllowedDomainBaseline>();

  currencies: DbCurrencyModel[] = [];

  industryList: Record<string, string> = {
    Cosmetics: 'Cosmetics',
    Electronics: 'Electronics',
    FMCG: 'FMCG',
    Fashion: 'Fashion',
    'Food & Beverages': 'Food & Beverages',
    Gaming: 'Gaming',
    'Home Furniture': 'Home Furniture',
    Leisure: 'Leisure',
    Luxury: 'Luxury',
    Media: 'Media',
    'Pets & Accessories': 'Pets & Accessories',
    Retail: 'Retail',
    Services: 'Services',
    Sports: 'Sports',
    Travel: 'Travel',
    'Vice (Cannabis, Alcohol, etc.)': 'Vice (Cannabis, Alcohol, etc.)',
    'Watches & Jewelry': 'Watches & Jewelry',
  };

  broadcastResolutionSelect: Record<BroadcastResolution, string> = {
    [BroadcastResolution.Res1080p30fps]: '1920x1080 , 30 fps , 5000 kbps',
    [BroadcastResolution.Res1080p60fps]: '1920x1080, 60 fps, 6500 kbps',
    [BroadcastResolution.Res720p30fps]: '1280x720, 30 fps , 3420 kbps',
    [BroadcastResolution.Res720p60fps]: '1280x720, 60 fps, 4500 kbps',
  };

  private _currency: DbCurrencyModel;

  get currency() {
    return this._currency;
  }
  set currency(currency: DbCurrencyModel) {
    this._currency = currency;
    this.store.currency = currency.code;
  }

  get disableAdTimeout() {
    return this.store.featureFlags?.disableAdTimeout ?? false;
  }
  set disableAdTimeout(disableAdTimeout: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      disableAdTimeout,
    };
  }

  get embedPostSession() {
    return this.store.storeConfig?.embed?.embedPostSession ?? false;
  }
  set embedPostSession(embedPostSession: boolean) {
    this.store.storeConfig = {
      ...this.store.storeConfig,
      embed: {
        ...this.store.storeConfig?.embed,
        embedPostSession,
      },
    };
  }
  get embedPreSessionTimeFrame() {
    return this.store.storeConfig?.embed?.hoursBeforeSessionToEmbed?.toString() ?? '';
  }
  set embedPreSessionTimeFrame(embedPreSessionTimeFrame: string) {
    this.store.storeConfig = {
      ...this.store.storeConfig,
      embed: {
        ...this.store.storeConfig?.embed,
        hoursBeforeSessionToEmbed: Number.parseInt(
          embedPreSessionTimeFrame ?? DEFAULT_SESSION_START_TIME_FRAME_HOURS.toString()
        ),
      },
    };
  }
  get enableShoppableVideo() {
    return this.store.featureFlags?.enableShoppableVideo ?? false;
  }
  set enableShoppableVideo(enableShoppableVideo: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableShoppableVideo,
    };
  }

  get enableSocialImports() {
    return this.store.featureFlags?.enableSocialImports ?? false;
  }
  set enableSocialImports(enableSocialImports: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableSocialImports,
    };
  }

  get useInstagramConnect() {
    return this.store.featureFlags?.useInstagramConnect ?? false;
  }
  set useInstagramConnect(useInstagramConnect: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      useInstagramConnect,
    };
  }

  get useInstagramConnectOnlyForCarouselImports() {
    return this.store.featureFlags?.useInstagramConnectOnlyForCarouselImports ?? false;
  }
  set useInstagramConnectOnlyForCarouselImports(
    useInstagramConnectOnlyForCarouselImports: boolean
  ) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      useInstagramConnectOnlyForCarouselImports,
    };
  }

  get enableCarouselAutomations() {
    return this.store.featureFlags?.enableCarouselAutomations ?? false;
  }
  set enableCarouselAutomations(enableCarouselAutomations: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableCarouselAutomations,
    };
  }

  get enableCarouselExecutiveDashboard() {
    return this.store.featureFlags?.enableCarouselExecutiveDashboard ?? false;
  }
  set enableCarouselExecutiveDashboard(enableCarouselExecutiveDashboard: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableCarouselExecutiveDashboard,
    };
  }

  get enableCarouselOverviewRowMetrics() {
    return this.store.featureFlags?.enableCarouselOverviewRowMetrics ?? false;
  }
  set enableCarouselOverviewRowMetrics(enableCarouselOverviewRowMetrics: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableCarouselOverviewRowMetrics,
    };
  }

  get enableSwipeShop() {
    return this.store.featureFlags?.enableSwipeShop ?? false;
  }
  set enableSwipeShop(enableSwipeShop: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableSwipeShop,
    };
  }

  get pictureInPicture() {
    return this.store.pictureInPicture;
  }
  set pictureInPicture(pictureInPicture: boolean) {
    this.store.pictureInPicture = pictureInPicture;
  }

  get maximumTimelineVideoLength(): number | undefined {
    return this.store.maximumTimelineVideoLength;
  }
  set maximumTimelineVideoLength(maximumTimelineVideoLength: number) {
    this.store.maximumTimelineVideoLength = maximumTimelineVideoLength;
  }

  get broadcastResolution() {
    return this.store.broadcastResolution;
  }

  set broadcastResolution(broadcastResolution: BroadcastResolution) {
    this.store.broadcastResolution = broadcastResolution;
  }

  get socialImportsMaxOnDemandRequestsDaily() {
    return this.store.socialImportsMaxOnDemandRequestsDaily ?? 3;
  }

  set socialImportsMaxOnDemandRequestsDaily(value: number) {
    this.store.socialImportsMaxOnDemandRequestsDaily = value;
  }

  readonly socialImportsItemsPerImportLimits = { max: 100, min: 1 };

  get socialImportsMaxItemsPerImport(): number | null {
    return this.store.socialImportsMaxItemsPerImport ?? null;
  }

  set socialImportsMaxItemsPerImport(value: number | null) {
    this.store.socialImportsMaxItemsPerImport =
      typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  get hostVideoGrid() {
    return this.store.hostVideoGrid ?? true;
  }
  set hostVideoGrid(hostVideoGrid: boolean) {
    this.store.hostVideoGrid = hostVideoGrid;
  }
  public get externalUrl() {
    return this.store.externalUrl;
  }
  public set externalUrl(value) {
    if (value) {
      const brandStoreUrlDomain = sanitizeUrl(value, { removePath: true }) as string;
      if (brandStoreUrlDomain) {
        this.store.externalUrl = brandStoreUrlDomain;
      }
    }
  }
  public get internalUrl() {
    return this.store.url;
  }
  public set internalUrl(value: string) {
    if (this.store.id === '8CQbmrU72NNRqLGQvA4y') {
      this.store.url = value;
      return;
    }
    this.store.url = sanitizeStoreInternalURL(value);
  }

  public set industry(industry: string) {
    this.store.industry = industry;
  }
  public get industry() {
    return this.store.industry;
  }

  @Input() storeId = '';

  store = new Store();
  integration = new Integration();

  get haveTheme() {
    return !!this.store.theme && Object.keys(this.store.theme).length > 0;
  }
  set haveTheme(value: boolean) {
    const storeTheme =
      Object.keys(this.store.theme ?? {}).length > 0
        ? this.store.theme
        : ({ primaryColor: '#000000', secondaryColor: '#000000' } as CustomTheme);

    this.store.theme = value ? storeTheme : ({} as CustomTheme);
  }
  get theme() {
    return this.store.theme ?? ({} as CustomTheme);
  }
  set theme(theme: CustomTheme) {
    this.store.theme = theme;
  }

  get showCameraSelectOnMobile() {
    return this.store.featureFlags?.showCameraSelectOnMobile ?? false;
  }
  set showCameraSelectOnMobile(showCameraSelectOnMobile: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      showCameraSelectOnMobile,
    };
  }

  get requireLoginForCarouselPollVote() {
    return this.store.featureFlags?.requireLoginForCarouselPollVote ?? false;
  }
  set requireLoginForCarouselPollVote(requireLoginForCarouselPollVote: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      requireLoginForCarouselPollVote,
    };
  }

  get timelineAds() {
    return this.store.featureFlags?.timelineAds ?? false;
  }

  set timelineAds(timelineAds: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      timelineAds,
    };
  }

  get horizontalCarouselAds() {
    return this.store.featureFlags?.horizontalCarouselAds ?? false;
  }
  set horizontalCarouselAds(horizontalCarouselAds: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      horizontalCarouselAds,
    };
  }

  get enableTalkingPointsAnalysis() {
    return this.store.featureFlags?.enableTalkingPointsAnalysis ?? false;
  }
  set enableTalkingPointsAnalysis(enableTalkingPointsAnalysis: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableTalkingPointsAnalysis,
    };
  }

  get enableTranscription() {
    return this.store.featureFlags?.enableTranscription ?? false;
  }
  set enableTranscription(enableTranscription: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableTranscription,
    };
  }

  get enableLiveSessionIntelligenceAnalysis() {
    return this.store.featureFlags?.enableLiveSessionIntelligenceAnalysis ?? false;
  }
  set enableLiveSessionIntelligenceAnalysis(enableLiveSessionIntelligenceAnalysis: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableLiveSessionIntelligenceAnalysis,
    };
  }

  get enableHub() {
    return this.store.featureFlags?.enableHub ?? false;
  }
  set enableHub(enableHub: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableHub,
    };
  }

  get enableLiveForum() {
    return this.store.featureFlags?.enableLiveForum ?? false;
  }
  set enableLiveForum(enableLiveForum: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableLiveForum,
    };
  }

  get enableLiveForumIpAllowlist() {
    return this.store.featureFlags?.enableLiveForumIpAllowlist ?? false;
  }
  set enableLiveForumIpAllowlist(enableLiveForumIpAllowlist: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableLiveForumIpAllowlist,
    };
  }

  get tiendaNubeApplicationAccess() {
    return this.store.featureFlags?.tiendaNubeApplicationAccess ?? false;
  }
  set tiendaNubeApplicationAccess(tiendaNubeApplicationAccess: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      tiendaNubeApplicationAccess,
    };
  }

  get muteOnMinimize() {
    return this.store.featureFlags?.muteOnMinimize ?? false;
  }
  set muteOnMinimize(muteOnMinimize: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      muteOnMinimize,
    };
  }

  get enableHorizontalMode() {
    return this.store.featureFlags?.enableHorizontalMode ?? false;
  }
  set enableHorizontalMode(enableHorizontalMode: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableHorizontalMode,
    };
  }

  get displayExternalVideoInputs() {
    return this.store.featureFlags?.displayExternalVideoInputs ?? false;
  }
  set displayExternalVideoInputs(displayExternalVideoInputs: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      displayExternalVideoInputs,
    };
  }

  get enableAISuggestionsInLiveSession() {
    return this.store.featureFlags?.enableAISuggestionsInLiveSession ?? false;
  }

  set enableAISuggestionsInLiveSession(enableAISuggestionsInLiveSession: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableAISuggestionsInLiveSession,
    };
  }

  get enableMediaGateway() {
    return this.store.featureFlags?.enableMediaGateway ?? false;
  }

  set enableMediaGateway(enableMediaGateway: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableMediaGateway,
    };
  }

  get enableStreaming() {
    return this.store.featureFlags?.enableStreaming ?? false;
  }
  set enableStreaming(enableStreaming: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableStreaming,
    };
  }

  get enableYoutubeStreaming() {
    return this.store.featureFlags?.enableYoutubeStreaming ?? false;
  }
  set enableYoutubeStreaming(enableYoutubeStreaming: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableYoutubeStreaming,
    };
  }

  get enableStripeAdminBilling() {
    return this.store.featureFlags?.enableStripeAdminBilling ?? false;
  }
  set enableStripeAdminBilling(enableStripeAdminBilling: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableStripeAdminBilling,
    };
  }

  get supportedScheduledIntegration(): boolean {
    if (this.isSfccIntegration) {
      return true;
    }
    if (isShopifyIntegrationClass(this.integration.integrationClass)) {
      return true;
    }
    if (this.integration.integrationClass === GENERIC_CSV_INTEGRATION_CLASS) {
      return !this.integration.productCatalogUrl?.startsWith('gs://');
    }
    if (this.isNaturaOrAvonBrazilIntegration) {
      return true;
    }
    return this.integration.integrationClass === URBANICA_INTEGRATION_CLASS;
  }

  get isCronScheduleEditable(): boolean {
    return this.supportedScheduledIntegration && !this.hasShopifyAppInstalled;
  }

  get enableCountdownBuffer() {
    return this.store.featureFlags?.enableCountdownBuffer ?? false; // Default to false (30s buffer disabled)
  }
  set enableCountdownBuffer(enableCountdownBuffer: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableCountdownBuffer,
    };
  }

  get mapBrandNameToProductName() {
    return this.store.featureFlags?.mapBrandNameToProductName ?? false;
  }
  set mapBrandNameToProductName(mapBrandNameToProductName: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      mapBrandNameToProductName,
    };
  }

  get carouselsMediaStartsMuted() {
    return this.store.featureFlags?.carouselsMediaStartsMuted ?? false;
  }
  set carouselsMediaStartsMuted(carouselsMediaStartsMuted: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      carouselsMediaStartsMuted,
    };
  }

  get showDisplayVerticalNavDesktop() {
    return this.store.featureFlags?.showDisplayVerticalNavDesktop ?? false;
  }
  set showDisplayVerticalNavDesktop(showDisplayVerticalNavDesktop: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      showDisplayVerticalNavDesktop,
    };
  }

  get showDisplayVerticalNavMobile() {
    return this.store.featureFlags?.showDisplayVerticalNavMobile ?? false;
  }
  set showDisplayVerticalNavMobile(showDisplayVerticalNavMobile: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      showDisplayVerticalNavMobile,
    };
  }

  get useBrandedPollsDomain() {
    return this.store.featureFlags?.useBrandedPollsDomain ?? false;
  }
  set useBrandedPollsDomain(useBrandedPollsDomain: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      useBrandedPollsDomain,
    };
  }

  get carouselAdReports() {
    return this.store.featureFlags?.carouselAdReports ?? false;
  }
  set carouselAdReports(carouselAdReports: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      carouselAdReports,
    };
  }

  get carouselTopButtonsLayout() {
    return this.store.featureFlags?.carouselTopButtonsLayout ?? false;
  }
  set carouselTopButtonsLayout(carouselTopButtonsLayout: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      carouselTopButtonsLayout,
    };
  }

  get carouselSocialImportsEvery30Minutes() {
    return this.store.featureFlags?.carouselSocialImportsEvery30Minutes ?? false;
  }
  set carouselSocialImportsEvery30Minutes(value: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      carouselSocialImportsEvery30Minutes: value,
    };
  }

  get importInstagramCarousels() {
    return this.store.featureFlags?.importInstagramCarousels ?? (this.storeId ? false : true);
  }
  set importInstagramCarousels(importInstagramCarousels: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      importInstagramCarousels,
    };
  }

  get smartContentCarousels() {
    return this.store.featureFlags?.smartContentCarousels ?? false;
  }
  set smartContentCarousels(smartContentCarousels: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      smartContentCarousels,
    };
  }

  get tinderSwipe() {
    return this.store.featureFlags?.tinderSwipe ?? false;
  }
  set tinderSwipe(tinderSwipe: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      tinderSwipe,
    };
  }

  get enableSmartStrategy() {
    return this.store.featureFlags?.enableSmartStrategy ?? false;
  }
  set enableSmartStrategy(enableSmartStrategy: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableSmartStrategy,
    };
  }

  get enableAiVideoCategorization() {
    return this.store.featureFlags?.enableAiVideoCategorization ?? false;
  }
  set enableAiVideoCategorization(enableAiVideoCategorization: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      enableAiVideoCategorization,
    };
  }

  get voteResultsAfterVote() {
    return this.store.featureFlags?.voteResultsMode === VoteResultsMode.AfterVote;
  }
  set voteResultsAfterVote(voteResultsAfterVote: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      voteResultsMode: voteResultsAfterVote ? VoteResultsMode.AfterVote : VoteResultsMode.Always,
    };
  }

  get frenchTimestampFormat() {
    return this.store.featureFlags?.frenchTimestampFormat ?? false;
  }
  set frenchTimestampFormat(frenchTimestampFormat: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      frenchTimestampFormat,
    };
  }

  private get lazyEmbed() {
    return this.store.featureFlags?.lazyEmbed ?? DefaultLazyEmbed;
  }

  get lazyEmbedCarouselEnabled() {
    return this.lazyEmbed.enabled;
  }
  set lazyEmbedCarouselEnabled(enabled: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      lazyEmbed: { ...this.lazyEmbed, enabled },
    };
  }

  get lazyEmbedCarouselDesktop() {
    return this.lazyEmbed.desktop;
  }
  set lazyEmbedCarouselDesktop(desktop: number) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      lazyEmbed: { ...this.lazyEmbed, desktop },
    };
  }

  get lazyEmbedCarouselMobile() {
    return this.lazyEmbed.mobile;
  }
  set lazyEmbedCarouselMobile(mobile: number) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      lazyEmbed: { ...this.lazyEmbed, mobile },
    };
  }

  get lazyVideoPreload() {
    return this.store.featureFlags?.lazyVideoPreload ?? false;
  }
  set lazyVideoPreload(lazyVideoPreload: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      lazyVideoPreload,
    };
  }

  get ctaClickedPostMessage() {
    return this.store.featureFlags?.ctaClickedPostMessage ?? false;
  }
  set ctaClickedPostMessage(ctaClickedPostMessage: boolean) {
    this.store.featureFlags = {
      ...this.store.featureFlags,
      ctaClickedPostMessage,
    };
  }

  constructor(
    public modal: NgbActiveModal,
    private modalService: NgbModal,
    private csvFeedPreviewService: CsvFeedPreviewService,
    private storeCatalogUploadService: StoreCatalogUploadService,
    private storesService: StoresService,
    private tiendanubeAppService: TiendanubeAppService,
    private toastr: ToastrService,
    private usersService: UsersService
  ) {}

  get isGenericCsvIntegration(): boolean {
    return this.integration.integrationClass === GENERIC_CSV_INTEGRATION_CLASS;
  }

  get isSfccIntegration(): boolean {
    return this.integration.integrationClass === SFCC_INTEGRATION_CLASS;
  }

  get sfccValidationErrors(): string[] {
    if (!this.isSfccIntegration) {
      return [];
    }
    return validateSfccIntegrationConfig(this.integration.sfccIntegrationConfig);
  }

  get isNaturaOrAvonBrazilIntegration(): boolean {
    return (
      this.integration.integrationClass === NATURA_BRAZIL_INTEGRATION_CLASS ||
      this.integration.integrationClass === AVON_BRAZIL_INTEGRATION_CLASS
    );
  }

  get csvValidationErrors(): string[] {
    if (!this.isGenericCsvIntegration) {
      return [];
    }
    return validateCsvIntegrationConfig(this.integration.csvIntegrationConfig);
  }

  ngOnInit() {
    this.setupCsvFeedPreviewSubscription();
    this.paymentSuppliers = this.getVisiblePaymentSuppliers();

    if (!this.storeId) {
      this.isLoading = false;
      this.integration.cronScheduledSync = 'None';
      this.broadcastResolution = DEFAULT_BROADCAST_RESOLUTION;
      this.embedPostSession = true;
      this.showDisplayVerticalNavDesktop = true;
      this.loadCurrencies()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((currencies: DbCurrencyModel[]) => (this.currencies = currencies));
      return;
    }

    this.loadStoreAndCurrencies();
  }

  ngOnDestroy(): void {
    if (this.devClickTimer) {
      clearTimeout(this.devClickTimer);
      this.devClickTimer = null;
    }
    this.csvFeedPreviewRequest$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onIntegrationClassChange(integrationClass: string): void {
    const previousIntegrationClass = this.integration.integrationClass;
    this.integration.integrationClass = integrationClass;

    if (this.isGenericCsvIntegration) {
      this.store.externalCart = true;
      this.ensureCsvIntegrationConfig();
      if (this.integration.productCatalogUrl?.trim()) {
        this.onProductCatalogUrlChange(this.integration.productCatalogUrl);
      }
    } else {
      this.clearCsvIntegrationState();
    }

    if (isEnteringSfccIntegration(previousIntegrationClass, integrationClass)) {
      this.integration.sfccIntegrationConfig = applySfccIntegrationDefaults(
        this.integration.sfccIntegrationConfig
      );
      this.integration.productIds = [];
      this.productIds = '';
    } else if (!this.isSfccIntegration) {
      this.clearSfccIntegrationState();
    }

    if (this.isNaturaOrAvonBrazilIntegration) {
      this.store.paymentSupplier = PaymentSuppliers.SDK;
      this.paymentSuppliers = this.getVisiblePaymentSuppliers(PaymentSuppliers.SDK);
    }

    this.applyCronSchedulePolicy();
  }

  public onProductCatalogUrlChange(url: string): void {
    if (!this.isGenericCsvIntegration || !url?.trim()) {
      this.csvFeedPreviewRequestId += 1;
      this.resetCsvFeedPreview();
      return;
    }

    this.csvFeedPreviewRequest$.next(url.trim());
  }

  public onCsvCatalogFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    void this.uploadCsvCatalog(file);
  }

  private async uploadCsvCatalog(file: File): Promise<void> {
    const storeId = this.storeId || this.store.id;
    if (!storeId) {
      this.toastr.error('Save the store before uploading a catalog file.');
      return;
    }

    this.isCsvCatalogUploading = true;
    this.csvCatalogUploadProgress = 0;

    try {
      const catalogUrl = await this.storeCatalogUploadService.uploadCatalog(
        storeId,
        file,
        (progress) => {
          this.csvCatalogUploadProgress = progress;
        }
      );

      this.integration.productCatalogUrl = catalogUrl;
      this.onProductCatalogUrlChange(catalogUrl);
      this.toastr.success('Catalog uploaded successfully.');
    } catch (error) {
      this.toastr.error(error instanceof Error ? error.message : 'Catalog upload failed.');
    } finally {
      this.isCsvCatalogUploading = false;
      this.csvCatalogUploadProgress = 0;
    }
  }

  public getCsvFieldMapping(field: CsvTerrificField): string | null {
    return this.integration.csvIntegrationConfig?.fieldMapping?.[field] ?? null;
  }

  public setCsvFieldMapping(field: CsvTerrificField, header: string | null): void {
    this.ensureCsvIntegrationConfig();
    if (!header) {
      delete this.integration.csvIntegrationConfig!.fieldMapping[field];
      return;
    }

    this.integration.csvIntegrationConfig!.fieldMapping[field] = header;
  }

  public getCsvVariantGroupingStrategy(): CsvVariantGroupingStrategy {
    this.ensureCsvIntegrationConfig();
    return this.integration.csvIntegrationConfig!.variantGrouping.strategy;
  }

  public setCsvVariantGroupingStrategy(strategy: CsvVariantGroupingStrategy): void {
    this.ensureCsvIntegrationConfig();
    this.integration.csvIntegrationConfig!.variantGrouping.strategy = strategy;
    if (strategy !== 'customField') {
      delete this.integration.csvIntegrationConfig!.variantGrouping.customField;
    }
  }

  public getCsvVariantGroupingCustomField(): string | undefined {
    return this.integration.csvIntegrationConfig?.variantGrouping?.customField;
  }

  public setCsvVariantGroupingCustomField(customField: string | null | undefined): void {
    this.ensureCsvIntegrationConfig();
    if (!customField) {
      delete this.integration.csvIntegrationConfig!.variantGrouping.customField;
      return;
    }

    this.integration.csvIntegrationConfig!.variantGrouping.customField = customField;
  }

  private setupCsvFeedPreviewSubscription(): void {
    this.csvFeedPreviewRequest$
      .pipe(debounceTime(500), takeUntil(this.destroyed$))
      .subscribe((url) => {
        void this.loadCsvFeedPreview(url);
      });
  }

  private ensureCsvIntegrationConfig(): void {
    if (!this.integration.csvIntegrationConfig) {
      this.integration.csvIntegrationConfig = {
        delimiter: 'auto',
        fieldMapping: {},
        variantGrouping: { strategy: 'externalUrl' },
      };
      return;
    }

    if (!this.integration.csvIntegrationConfig.variantGrouping) {
      this.integration.csvIntegrationConfig.variantGrouping = { strategy: 'externalUrl' };
    }
  }

  private clearCsvIntegrationState(): void {
    delete this.integration.csvIntegrationConfig;
    this.csvFeedPreviewRequestId += 1;
    this.resetCsvFeedPreview();
  }

  private clearSfccIntegrationState(): void {
    delete this.integration.sfccIntegrationConfig;
  }

  private applyCronSchedulePolicy(): void {
    if (!this.isCronScheduleEditable) {
      this.integration.cronScheduledSync = 'None';
      delete this.integration.cronScheduledSyncTimezone;
    }
  }

  private resetCsvFeedPreview(): void {
    this.csvFeedPreviewStatus = 'idle';
    this.csvFeedPreviewError = '';
    this.csvFeedHeaders = [];
    this.csvFeedSampleRowCount = 0;
  }

  private async loadCsvFeedPreview(url: string): Promise<void> {
    const requestId = ++this.csvFeedPreviewRequestId;
    this.csvFeedPreviewStatus = 'loading';
    this.csvFeedPreviewError = '';

    try {
      const result = await this.csvFeedPreviewService.previewFeed(
        url,
        this.storeId || this.store.id
      );
      if (requestId !== this.csvFeedPreviewRequestId) {
        return;
      }

      this.csvFeedHeaders = result.headers;
      this.csvFeedSampleRowCount = result.sampleRowCount;
      this.ensureCsvIntegrationConfig();

      if (this.integration.csvIntegrationConfig) {
        this.integration.csvIntegrationConfig.delimiter = result.delimiter;
        this.integration.csvIntegrationConfig = applyCsvFeedPresets(
          result.headers,
          this.integration.csvIntegrationConfig
        );
      }

      this.csvFeedPreviewStatus = 'success';
    } catch (error) {
      if (requestId !== this.csvFeedPreviewRequestId) {
        return;
      }

      this.csvFeedPreviewStatus = 'error';
      this.csvFeedPreviewError = error instanceof Error ? error.message : 'Could not fetch feed';
      this.csvFeedHeaders = [];
      this.csvFeedSampleRowCount = 0;
    }
  }

  private loadStoreAndCurrencies(): void {
    forkJoin({
      currencies: this.loadCurrencies(),
      hasShopifyAppInstalled: this.storesService.hasShopifyAppInstalled(this.storeId),
      shopifyStoresIds: this.storesService.getShopifyStoreIdsThatHaveIntegration(),
      store: this.storesService.getStoreById(this.storeId),
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        error: () => {
          console.error('Error loading store and currencies');
          this.modal.dismiss();
        },
        next: (values) => {
          this.currencies = values.currencies;
          this.hasShopifyAppInstalled = values.hasShopifyAppInstalled;
          const store = values.store;
          const integration = store?.integration;
          this.storesIdsThatSupportColorPalate = [
            ...this.storesIdsThatSupportColorPalate,
            ...values.shopifyStoresIds,
          ];

          if (integration) {
            const integrationDoc = new Integration({
              ...integration,
              createdAt: new Date(integration.createdAt),
              updatedAt: new Date(integration.updatedAt),
            });
            this.integration = integrationDoc;
            this.productIds =
              this.integration.productIds.length > 0 ? this.integration.productIds.join(',') : '';
            this.initializeLoadedIntegrationConfig();
          }

          if (store) {
            Object.assign(this.store, store, { allowedDomains: store.allowedDomains ?? [] });
            this.store.id = store.id ?? this.storeId;
            this.hadTiendaNubeApplicationAccess = this.tiendaNubeApplicationAccess;
            this.captureAllowedDomainBaselines(this.store.allowedDomains ?? []);

            const currency = this.currencies.find((cur: DbCurrencyModel) => {
              return cur.code === this.store.currency;
            });
            if (currency) {
              this.currency = currency;
            }

            this.store.storeType = this.store.storeType ?? StoreType.LIVE;
            this.store.industry = this.store.industry ?? '';
            this.broadcastResolution = this.broadcastResolution ?? DEFAULT_BROADCAST_RESOLUTION;
            this.paymentSuppliers = this.getVisiblePaymentSuppliers(this.store.paymentSupplier);
            this.hadInitialPaymentSupplier = !!this.store.paymentSupplier;
            if (
              values.shopifyStoresIds.includes(this.store.id) &&
              this.store.colorPalateSelector === undefined
            ) {
              this.store.colorPalateSelector = true;
            }

            this.isLoading = false;
          } else {
            this.toastr.error("Can't load store.");
            this.modal.dismiss();
          }
        },
      });
  }

  private loadCurrencies(): Observable<DbCurrencyModel[]> {
    return this.storesService
      .getAllCurrencies()
      .pipe(map((res) => res.docs.map((doc) => doc.data())));
  }

  public onStoreCategoryChange(category: StoreCategory): void {
    this.store.storeCategory = category;
    if (!this.hadInitialPaymentSupplier) {
      this.store.paymentSupplier =
        category === StoreCategory.Media ? PaymentSuppliers.None : PaymentSuppliers.SDKWithRedirect;
    }
  }

  public openCronExpressionEditModal(): void {
    const modal = this.modalService.open(CronExpressionEditModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'dark-modal ltr',
    });
    modal.componentInstance.initialCronExpression = this.integration.cronScheduledSync;
    modal.componentInstance.initialTimezone = this.integration.cronScheduledSyncTimezone;
    void modal.result.then((value: CronExpressionEditResult | null) => {
      if (value) {
        this.integration.cronScheduledSync = value.cronExpression;
        if (value.timezone) {
          this.integration.cronScheduledSyncTimezone = value.timezone;
        } else {
          delete this.integration.cronScheduledSyncTimezone;
        }
      }
      return value;
    });
  }

  public async save(f: NgForm) {
    if (f.invalid) {
      TwoRaffleHelpers.markFormInvalidAndFocus(f);
      return;
    }

    const integrationConfigErrors = [...this.csvValidationErrors, ...this.sfccValidationErrors];
    if (integrationConfigErrors.length > 0) {
      this.toastr.error(integrationConfigErrors.join('; '));
      return;
    }

    this.applyCronSchedulePolicy();
    if (this.isCronScheduleEditable) {
      const cronValidation = validateCronScheduledSync(this.integration.cronScheduledSync);
      if (!cronValidation.valid) {
        this.toastr.error(cronValidation.message);
        return;
      }

      const timezoneValidation = validateCronScheduledSyncTimezone(
        this.integration.cronScheduledSyncTimezone,
        this.integration.cronScheduledSync
      );
      if (!timezoneValidation.valid) {
        this.toastr.error(timezoneValidation.message);
        return;
      }
    }

    this.store.ipAllowlist = this.store.ipAllowlist?.filter((entry) => entry.trim().length > 0);
    const ipAllowlistValidation = validateIpAllowlist(
      this.enableLiveForumIpAllowlist,
      this.store.ipAllowlist
    );
    if (!ipAllowlistValidation.valid) {
      this.toastr.error(ipAllowlistValidation.message);
      return;
    }

    this.isSubmitting = true;

    if (this.productIds) {
      const productIds = this.productIds.split(',');
      this.integration.productIds = productIds;
    }

    const sanitizedWebsiteUrl = this.getSanitizedWebsiteUrl();
    if (!sanitizedWebsiteUrl) {
      this.toastr.error('Invalid store website URL');
      this.isSubmitting = false;
      return;
    }
    this.integration.websiteUrl = sanitizedWebsiteUrl;

    let storeExists = false;
    try {
      if (this.store.id) {
        storeExists = await this.storesService.storeExists(this.store.id);
      }
    } catch (error) {
      console.error(error);
      this.toastr.error(`Failed to validate store existence. Please try again.`);
      this.isSubmitting = false;
      return;
    }

    f.form.disable();

    this.storesService.cleanIntegrationData(this.integration);
    this.integration.productFlags = Object.assign({}, { ...this.integration.productFlags });

    // Always persist these flags in store payload, even when untouched in UI.
    this.store.featureFlags = {
      ...this.store.featureFlags,
      importInstagramCarousels: this.importInstagramCarousels,
    };

    this.store.externalUrl = this.integration.websiteUrl;
    this.store.checkoutRedirectRoute = `/${this.integration.cartPath}`;
    this.storesService
      .updateStore(Object.assign({}, { ...this.store, integration: this.integration }), storeExists)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          f.form.enable();
        })
      )
      // eslint-disable-next-line sonarjs/deprecation
      .subscribe(
        (storeId) => {
          if (!storeId) {
            this.toastr.error(`Failed to save store: missing store id from Platform response.`);
            return;
          }

          // Platform is source of truth for storeId on create
          this.store.id = storeId;
          this.storeId = storeId;

          void this.maybeSyncProductsOnTiendanubeAccessGrant(storeId);

          this.toastr.success('Action was completed successfully');
          this.modal.close();
        },
        (err) => {
          this.toastr.error(`Failed to save store. ${err.error?.errors?.[0] ?? err.message}`);
        }
      );
  }

  private async maybeSyncProductsOnTiendanubeAccessGrant(storeId: string): Promise<void> {
    const accessJustGranted =
      !this.hadTiendaNubeApplicationAccess && this.tiendaNubeApplicationAccess;
    if (
      !this.tiendaNubeApplicationAccess ||
      this.integration.integrationClass !== TIENDANUBE_INTEGRATION_CLASS
    ) {
      return;
    }

    try {
      await this.tiendanubeAppService.ensureWebhooks(storeId);
      this.hadTiendaNubeApplicationAccess = true;
    } catch (error) {
      console.error(error);
      this.toastr.error(
        'Store saved, but Tiendanube webhook registration failed. Retry later or contact support.'
      );
    }

    if (!accessJustGranted) {
      return;
    }

    try {
      await this.storesService.syncIntegrationData(storeId);
    } catch (error) {
      console.error(error);
      this.toastr.error(
        'Store saved, but product sync failed to start. Retry sync from the stores list.'
      );
    }
  }

  private initializeLoadedIntegrationConfig(): void {
    if (this.isSfccIntegration) {
      this.integration.sfccIntegrationConfig = applySfccIntegrationDefaults(
        this.integration.sfccIntegrationConfig
      );
    } else {
      this.clearSfccIntegrationState();
    }

    if (!this.isGenericCsvIntegration) {
      this.clearCsvIntegrationState();
      return;
    }

    this.ensureCsvIntegrationConfig();
    if (this.integration.productCatalogUrl?.trim()) {
      this.onProductCatalogUrlChange(this.integration.productCatalogUrl);
    }
  }

  public delete(): void {}

  private getSanitizedWebsiteUrl(): string | null {
    return sanitizeUrl(this.integration.websiteUrl, {
      removePath: true,
      stripWWW: !this.isSfccIntegration,
    }) as string | null;
  }

  private coerceToDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;

    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    // Firestore Timestamp (duck-typed)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (value as any)?.toDate === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (value as any).toDate();
    }

    // Serialized timestamp-like shape (fallback)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (value as any)?.seconds === 'number') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new Date((value as any).seconds * 1000);
    }

    return null;
  }

  private getVisiblePaymentSuppliers(currentPaymentSupplier?: PaymentSuppliers | null) {
    const paymentSuppliers = [...VISIBLE_PAYMENT_SUPPLIERS];

    if (this.isNaturaOrAvonBrazilIntegration && !paymentSuppliers.includes(PaymentSuppliers.SDK)) {
      paymentSuppliers.push(PaymentSuppliers.SDK);
    }

    if (
      currentPaymentSupplier !== undefined &&
      currentPaymentSupplier !== null &&
      !paymentSuppliers.includes(currentPaymentSupplier)
    ) {
      paymentSuppliers.push(currentPaymentSupplier);
    }

    return paymentSuppliers.map((paymentSupplier) => ({
      name: PaymentSuppliers[paymentSupplier],
      value: paymentSupplier,
    }));
  }

  formatDate(timestamp: unknown): string {
    const date = this.coerceToDate(timestamp);
    if (!date) {
      return '';
    }

    return date.toLocaleString('en-US', {
      day: 'numeric',
      hour: 'numeric',
      hour12: true,
      minute: '2-digit',
      month: 'numeric',
      second: '2-digit',
      year: 'numeric',
    });
  }

  addNewAllowedDomain(): void {
    const userUid = this.getConnectedUserUid();
    const newItem: AllowedDomain = {
      addressPattern: 'https://',
      createdAt: new Date(),
      createdBy: userUid,
      id: v4(),
      isEnabled: true,
      updatedAt: new Date(),
      updatedBy: userUid,
    };

    this.store.allowedDomains = [...(this.store.allowedDomains || []), newItem];
  }

  onAllowedDomainAddressPatternChange(item: AllowedDomain, addressPattern: string): void {
    item.addressPattern = addressPattern;
    this.updateAllowedDomainAuditIfChanged(item);
  }

  onAllowedDomainEnabledChange(item: AllowedDomain, isEnabled: boolean): void {
    item.isEnabled = isEnabled;
    this.updateAllowedDomainAuditIfChanged(item);
  }

  private captureAllowedDomainBaselines(allowedDomains: AllowedDomain[]): void {
    this.allowedDomainBaselines.clear();
    for (const allowedDomain of allowedDomains) {
      this.allowedDomainBaselines.set(allowedDomain.id, {
        addressPattern: allowedDomain.addressPattern,
        isEnabled: allowedDomain.isEnabled,
        updatedAt: allowedDomain.updatedAt,
        updatedBy: allowedDomain.updatedBy,
      });
    }
  }

  private updateAllowedDomainAuditIfChanged(item: AllowedDomain): void {
    const baseline = this.allowedDomainBaselines.get(item.id);
    const hasChanged =
      !baseline ||
      baseline.addressPattern !== item.addressPattern ||
      baseline.isEnabled !== item.isEnabled;

    if (!hasChanged) {
      item.updatedAt = baseline.updatedAt;
      item.updatedBy = baseline.updatedBy;
      return;
    }

    item.updatedAt = new Date();
    item.updatedBy = this.getConnectedUserUid();
  }

  private getConnectedUserUid(): string {
    return this.usersService.getConnectedUserSync()?.uid || 'unknown-user';
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  removeItem(index: number): void {
    this.store.allowedDomains = this.store.allowedDomains?.filter((_, i) => i !== index);
  }

  addNewIpAllowlistEntry(): void {
    this.store.ipAllowlist = [...(this.store.ipAllowlist ?? []), ''];
  }

  onIpAllowlistEntryChange(index: number, value: string): void {
    this.store.ipAllowlist = this.store.ipAllowlist?.map((entry, i) =>
      i === index ? value.trim() : entry
    );
  }

  removeIpAllowlistEntry(index: number): void {
    this.store.ipAllowlist = this.store.ipAllowlist?.filter((_, i) => i !== index);
  }
}
